import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import { Info, Loader2, MessageSquare, TrendingUp, TrendingDown } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './ui/tooltip';
import api from '../lib/api';

// Sentiment colors
const SENTIMENT_COLORS = {
  positive: { bg: 'bg-emerald-100', text: 'text-emerald-800', border: 'border-emerald-300' },
  neutral: { bg: 'bg-slate-100', text: 'text-slate-700', border: 'border-slate-300' },
  mixed: { bg: 'bg-amber-100', text: 'text-amber-800', border: 'border-amber-300' },
  negative: { bg: 'bg-red-100', text: 'text-red-800', border: 'border-red-300' }
};

export default function ThemeSentimentMatrix({ simulationId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCell, setSelectedCell] = useState(null);

  useEffect(() => {
    const fetchMatrix = async () => {
      if (!simulationId) return;

      try {
        setLoading(true);
        const response = await api.get(`/simulations/${simulationId}/theme-sentiment-matrix`);
        setData(response.data);
        setError(null);
      } catch (err) {
        setError(err.response?.data?.detail || err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchMatrix();
  }, [simulationId]);

  if (loading) {
    return (
      <Card className="border border-slate-200">
        <CardContent className="p-6 text-center">
          <Loader2 className="w-8 h-8 text-slate-400 mx-auto mb-2 animate-spin" />
          <p className="text-sm text-slate-600">Loading theme-sentiment matrix...</p>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertDescription>
          {error.includes('open-ended') ? (
            <span>This analysis is only available for open-ended simulations.</span>
          ) : (
            <span>Error loading matrix: {error}</span>
          )}
        </AlertDescription>
      </Alert>
    );
  }

  if (!data || !data.themes || data.themes.length === 0) {
    return (
      <Alert>
        <Info className="h-4 w-4" />
        <AlertDescription>
          No theme data available for this simulation.
        </AlertDescription>
      </Alert>
    );
  }

  const { matrix, themes, sentiments, sentiment_distribution, total_responses } = data;

  // Get cell color based on count and confidence
  const getCellColor = (count, avgConfidence) => {
    if (count === 0) return 'bg-slate-50 text-slate-400';
    
    // Intensity based on count
    const pct = (count / total_responses) * 100;
    
    if (avgConfidence >= 0.7) {
      if (pct >= 15) return 'bg-blue-600 text-white font-semibold';
      if (pct >= 8) return 'bg-blue-400 text-white';
      if (pct >= 4) return 'bg-blue-300 text-slate-800';
      return 'bg-blue-100 text-slate-700';
    } else if (avgConfidence >= 0.5) {
      if (pct >= 15) return 'bg-blue-500 text-white font-semibold';
      if (pct >= 8) return 'bg-blue-300 text-white';
      if (pct >= 4) return 'bg-blue-200 text-slate-800';
      return 'bg-blue-100 text-slate-700';
    } else {
      if (pct >= 15) return 'bg-slate-400 text-white';
      if (pct >= 8) return 'bg-slate-300 text-slate-800';
      return 'bg-slate-200 text-slate-700';
    }
  };

  return (
    <Card className="border border-slate-200">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-slate-600" />
              Theme × Sentiment × Confidence Matrix
            </CardTitle>
            <p className="text-sm text-slate-500 mt-1">
              Explore thematic patterns across sentiment with confidence levels
            </p>
          </div>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="w-4 h-4 text-slate-400 cursor-help" />
              </TooltipTrigger>
              <TooltipContent side="left" className="max-w-xs">
                <p className="text-xs">
                  Each cell shows the count of responses for that theme-sentiment combination.
                  Cell intensity indicates frequency. Darker purple = higher confidence.
                  Click cells to see example quotes.
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>

        {/* Sentiment Distribution Summary */}
        <div className="flex items-center gap-2 mt-3 flex-wrap">
          <span className="text-xs text-slate-600 font-medium">Overall Sentiment:</span>
          {sentiments.map(sentiment => (
            <Badge 
              key={sentiment} 
              variant="outline" 
              className={`text-xs ${SENTIMENT_COLORS[sentiment].bg} ${SENTIMENT_COLORS[sentiment].text} ${SENTIMENT_COLORS[sentiment].border}`}
            >
              {sentiment}: {sentiment_distribution[sentiment] || 0}
            </Badge>
          ))}
        </div>
      </CardHeader>

      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-xs border-collapse">
            <thead>
              <tr className="border-b-2 border-slate-300">
                <th className="text-left p-2 font-semibold text-slate-700 bg-slate-50 sticky left-0 z-10 min-w-[140px]">
                  Theme \ Sentiment
                </th>
                {sentiments.map((sentiment, idx) => (
                  <th 
                    key={idx}
                    className={`text-center p-2 font-semibold bg-slate-50 min-w-[100px] ${SENTIMENT_COLORS[sentiment].text}`}
                  >
                    <div className="flex items-center justify-center gap-1">
                      {sentiment === 'positive' && <TrendingUp className="w-3 h-3" />}
                      {sentiment === 'negative' && <TrendingDown className="w-3 h-3" />}
                      <span className="capitalize">{sentiment}</span>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {themes.map((theme, rowIdx) => (
                <tr key={rowIdx} className="border-b border-slate-200">
                  <td className="p-2 font-medium text-slate-800 bg-slate-50/50 sticky left-0 z-10">
                    <div className="flex items-center gap-1">
                      <span className="truncate" title={theme}>{theme}</span>
                    </div>
                  </td>
                  {sentiments.map((sentiment, colIdx) => {
                    const cellData = matrix[theme]?.[sentiment];
                    const count = cellData?.count || 0;
                    const avgConfidence = cellData?.avg_confidence || 0;
                    const examples = cellData?.examples || [];
                    const colorClass = getCellColor(count, avgConfidence);
                    
                    return (
                      <td key={colIdx} className="p-1">
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div 
                                className={`
                                  rounded px-2 py-2.5 text-center transition-all 
                                  hover:scale-105 hover:shadow-sm cursor-pointer
                                  ${colorClass}
                                `}
                                onClick={() => count > 0 && setSelectedCell({ theme, sentiment, cellData })}
                              >
                                {count > 0 ? (
                                  <>
                                    <div className="font-semibold">{count}</div>
                                    <div className="text-[10px] opacity-70 mt-0.5">
                                      {(avgConfidence * 100).toFixed(0)}% conf
                                    </div>
                                  </>
                                ) : (
                                  <span className="opacity-50">—</span>
                                )}
                              </div>
                            </TooltipTrigger>
                            {count > 0 && (
                              <TooltipContent side="top" className="max-w-xs">
                                <p className="text-xs font-semibold mb-1">
                                  {theme} × {sentiment}
                                </p>
                                <p className="text-xs mb-1">
                                  <strong>{count}</strong> responses ({((count / total_responses) * 100).toFixed(1)}%)
                                </p>
                                <p className="text-xs">
                                  Avg Confidence: <strong>{(avgConfidence * 100).toFixed(0)}%</strong>
                                </p>
                                {examples.length > 0 && (
                                  <p className="text-[10px] mt-2 italic text-slate-400">
                                    Click to view example quotes
                                  </p>
                                )}
                              </TooltipContent>
                            )}
                          </Tooltip>
                        </TooltipProvider>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Legend */}
        <div className="mt-4 space-y-2">
          <div className="flex items-center gap-4 text-xs text-slate-600 flex-wrap">
            <span className="font-medium">Confidence Legend:</span>
            <div className="flex items-center gap-1">
              <div className="w-5 h-4 bg-blue-600 rounded"></div>
              <span>High (&gt;70%)</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-5 h-4 bg-blue-500 rounded"></div>
              <span>Medium (50-70%)</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-5 h-4 bg-slate-400 rounded"></div>
              <span>Low (&lt;50%)</span>
            </div>
          </div>
          
          <p className="text-xs text-slate-500 italic">
            Cell color intensity indicates response frequency. Darker purple/blue indicates higher model confidence.
          </p>
        </div>

        {/* Selected Cell Details */}
        {selectedCell && (
          <div className="mt-4 p-4 bg-blue-50/50 rounded-lg border border-blue-200">
            <div className="flex items-start justify-between mb-3">
              <h4 className="text-sm font-semibold text-blue-900">
                {selectedCell.theme} × {selectedCell.sentiment}
              </h4>
              <button 
                onClick={() => setSelectedCell(null)}
                className="text-blue-600 hover:text-blue-800 text-xs font-medium"
              >
                Close
              </button>
            </div>
            
            <div className="space-y-2">
              <div className="flex items-center gap-4 text-xs">
                <span className="text-slate-600">
                  <strong>Count:</strong> {selectedCell.cellData.count}
                </span>
                <span className="text-slate-600">
                  <strong>Avg Confidence:</strong> {(selectedCell.cellData.avg_confidence * 100).toFixed(0)}%
                </span>
              </div>

              {selectedCell.cellData.examples && selectedCell.cellData.examples.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-slate-700 mb-2">Example Responses:</p>
                  <div className="space-y-2">
                    {selectedCell.cellData.examples.map((ex, idx) => (
                      <div key={idx} className="p-2 bg-white rounded border border-blue-100">
                        <p className="text-xs text-slate-700 italic">"{ex.text}..."</p>
                        <p className="text-[10px] text-slate-500 mt-1">
                          Confidence: {(ex.confidence * 100).toFixed(0)}%
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
